/**
 * Ando Mode — custom agent profile (user / org / global scope).
 *
 * Wire shape mirrors ``ando-frontend/app/types/agentModes.ts`` (snake_case,
 * matches backend Pydantic). Mode is locked per-conversation; versioning
 * is append-only with rollback (see ``current_version``).
 */

import { z } from "zod";
import { IsoTimestampSchema, UuidSchema } from "./common.js";

export const AgentModeScopeSchema = z.enum(["user", "org", "global"]);
export type AgentModeScope = z.infer<typeof AgentModeScopeSchema>;

/**
 * Quality/latency selector — May 26, 2026 two-tier collapse.
 *
 * - ``fast`` (default, ~5s) — single-shot answer.
 * - ``deep`` (~30-60s) — multi-step reasoning + extended thinking.
 *
 * Replaces the legacy ``AgentModeSearchPower`` 3-tier enum + the
 * ``thinking_mode`` boolean. The BE accepts both forms in parallel via
 * ``normalize_search_power`` for a transitional ≥30 day window.
 */
export const AgentModeModeSchema = z.enum(["fast", "deep"]);
export type AgentModeMode = z.infer<typeof AgentModeModeSchema>;

/** @deprecated May 26, 2026 — collapsed into ``AgentModeMode``. */
export const AgentModeSearchPowerSchema = z.enum(["quick", "standard", "expert"]);
/** @deprecated May 26, 2026 — collapsed into ``AgentModeMode``. */
export type AgentModeSearchPower = z.infer<typeof AgentModeSearchPowerSchema>;

export const FocusSchema = z.enum(["standard", "structured", "full"]);
export type Focus = z.infer<typeof FocusSchema>;

export const AgentModeDefaultConfigSchema = z.object({
  /** May 26, 2026 — two-tier mode selector. Replaces ``search_power`` +
   * ``thinking_mode``. New rows MUST emit only this field. */
  mode: AgentModeModeSchema.optional(),
  /** @deprecated May 26, 2026 — kept optional so inbound legacy payloads
   * still parse during the rollout window. New writes should emit ``mode``. */
  search_power: AgentModeSearchPowerSchema.optional(),
  /** @deprecated May 26, 2026 — absorbed into ``mode === "deep"``. */
  thinking_mode: z.boolean().optional(),
  web_search_mode: z.boolean().optional(),
  focus: FocusSchema.optional(),
  /** @deprecated May 2026 — use ``focus`` instead. */
  structured_data_mode: z.boolean().optional(),
});
export type AgentModeDefaultConfig = z.infer<typeof AgentModeDefaultConfigSchema>;

export const AgentModeVersionSchema = z.object({
  id: UuidSchema,
  agent_mode_id: UuidSchema,
  version_number: z.number().int().nonnegative(),
  system_prompt_overlay: z.string().nullable(),
  default_config: AgentModeDefaultConfigSchema,
  change_summary: z.string().nullable(),
  created_by_user_id: UuidSchema.nullable(),
  created_at: IsoTimestampSchema,
});
export type AgentModeVersion = z.infer<typeof AgentModeVersionSchema>;

export const ModeSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  scope: AgentModeScopeSchema,
  owner_user_id: UuidSchema.nullable().optional(),
  org_id: UuidSchema.nullable().optional(),
  is_active: z.boolean(),
  current_version: AgentModeVersionSchema,
  created_at: IsoTimestampSchema,
  updated_at: IsoTimestampSchema,
});
export type Mode = z.infer<typeof ModeSchema>;
